import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Space, Tag } from 'antd';
import { ContractInterface } from 'contract/type';
import dayjs from 'dayjs';
import React from 'react';
import { TableColumnInterface } from 'type/tableType';
import { PLATFORM_TYPES } from 'constants/constant';

interface TableColumnProps {
  handleOnClickTitle: (id: number) => void;
  handleOnClickSetting: (id: number) => void;
  handleOnClickDeleteButton: (id: number) => void;
  searchParams: URLSearchParams;
}

// Calculate remaining contract period
const calculateRemainingPeriod = (expireDate: string): string => {
  const now = dayjs();
  const expire = dayjs(expireDate);
  const diffInDays = expire.diff(now, 'day');

  if (diffInDays < 0) {
    return 'Expired';
  }

  if (diffInDays < 30) {
    return `${diffInDays} days`;
  }

  const months = Math.floor(diffInDays / 30);
  const remainingDays = diffInDays % 30;

  if (remainingDays === 0) {
    return `${months} months`;
  }

  return `${months} months, ${remainingDays} days`;
};

export const getContractColumns = ({
  handleOnClickTitle,
  handleOnClickSetting,
  handleOnClickDeleteButton,
  searchParams,
}: TableColumnProps): TableColumnInterface<ContractInterface>[] => {
  return [
    {
      title: 'Contract Name',
      dataIndex: 'title',
      key: 'title',
      sorter: (a: ContractInterface, b: ContractInterface) =>
        (a.title || '').localeCompare(b.title || ''),
      render: (value: string, record: ContractInterface): React.ReactNode => (
        <div>
          <a href="#" className="contract-title" onClick={() => handleOnClickTitle(record.id)}>
            <div>{record.title}</div>
            {record.title_en && (
              <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '2px' }}>
                {record.title_en}
              </div>
            )}
          </a>
        </div>
      ),
    },
    {
      title: 'Content Provider',
      dataIndex: 'licensor_title',
      key: 'licensor_title',
      sorter: (a: ContractInterface, b: ContractInterface) =>
        a.licensor_title.localeCompare(b.licensor_title),
      render: (value: string, record: ContractInterface): React.ReactNode => (
        <div>
          <div>{record.licensor_title}</div>
          {record.licensor_title_en && (
            <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '2px' }}>
              {record.licensor_title_en}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Service Platform',
      dataIndex: 'platforms',
      key: 'platforms',
      filters: PLATFORM_TYPES.map(platform => ({
        text: platform.label,
        value: platform.value,
      })),
      filteredValue: searchParams.get('platforms')
        ? searchParams.get('platforms')?.split(',')
        : null,
      render: (_text: string, record: ContractInterface): React.ReactNode => {
        const platforms = record.platforms;
        return (
          <div>
            {platforms && platforms.length > 0 ? (
              platforms.map((platform, index) => (
                <Tag key={index} color="blue" style={{ marginBottom: '2px' }}>
                  {platform}
                </Tag>
              ))
            ) : (
              <span style={{ color: '#8c8c8c' }}>No platforms</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Contract Period',
      dataIndex: 'contract_start_date',
      key: 'contract_period',
      render: (text: string, record: ContractInterface): React.ReactNode => (
        <div>
          <div style={{ fontSize: '12px', color: '#262626' }}>
            {dayjs(record.contract_start_date).format('YYYY-MM-DD')}
          </div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            ~ {dayjs(record.contract_expire_date).format('YYYY-MM-DD')}
          </div>
        </div>
      ),
      sorter: (a: ContractInterface, b: ContractInterface) =>
        dayjs(a.contract_expire_date).unix() - dayjs(b.contract_expire_date).unix(),
    },
    {
      title: 'Remaining Period',
      dataIndex: 'contract_expire_date',
      key: 'remaining_period',
      render: (text: string, record: ContractInterface): React.ReactNode => {
        const remaining = calculateRemainingPeriod(record.contract_expire_date);
        const isExpired = remaining === 'Expired';
        const isUrgent = remaining.includes('days') && !isExpired;

        return (
          <span
            style={{
              color: isExpired ? '#ff4d4f' : isUrgent ? '#fa8c16' : '#52c41a',
              fontWeight: isExpired || isUrgent ? 'bold' : 'normal',
            }}
          >
            {remaining}
          </span>
        );
      },
      sorter: (a: ContractInterface, b: ContractInterface) =>
        dayjs(a.contract_expire_date).unix() - dayjs(b.contract_expire_date).unix(),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      dataIndex: 'actions',
      width: 120,
      render: (_text: string, record: ContractInterface) => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleOnClickSetting(Number(record.id) || 0)}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleOnClickDeleteButton(Number(record.id) || 0)}
            className="action-btn delete-btn"
          />
        </Space>
      ),
    },
  ];
};
